import {useEffect, useRef} from 'react';

import {useIntl} from 'react-intl';

import {
  getCustomizeStepSlotKind,
  getStepTitle,
  getVisibleCustomizeSteps,
  getVisibleStepDisplayIndex,
  isMultiSlotCustomizeStep,
} from '@amblique/king-living/app/hooks/use-customize-steps';

/** Step is "active" when its top is at or above this offset from viewport top */
const HEADER_ACTIVE_OFFSET_PX = 100;

/** Scroll position below this = "at top" → show header first state */
const SCROLL_TOP_THRESHOLD = 20;
const SCROLL_BOTTOM_THRESHOLD = 80;

/**
 * Drives the panel header based on scroll position on the main steps screen.
 * When a step scrolls into the active zone, calls onHeaderChange with step info.
 * When scrolled back to top, calls onHeaderChange(null).
 *
 * @param {Object} params
 * @param {Array}    params.steps           - Step definitions from buildSteps()
 * @param {*}        params.activeSlot      - Non-null when in sub-view (disables scroll tracking)
 * @param {Object}   params.stepRefs        - Ref map of stepId → DOM element
 * @param {Object}   params.scrollBodyRef   - Ref to the scrollable container
 * @param {Function} params.onHeaderChange  - Callback to update header
 * @param {Function} params.setActiveStepId - State setter for active step ID
 */
export function useStepScrollHeader({
  steps,
  activeSlot,
  stepRefs,
  scrollBodyRef,
  onHeaderChange,
  setActiveStepId,
}) {
  const intl = useIntl();
  // Stable refs so scroll listener does not need to re-attach on every render
  const stepsRef = useRef(steps);
  const onHeaderChangeRef = useRef(onHeaderChange);
  const setActiveStepIdRef = useRef(setActiveStepId);
  // Track last emitted header to dedupe scroll updates (prevents re-render spam)
  const lastEmittedHeaderRef = useRef(null);

  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);
  useEffect(() => {
    onHeaderChangeRef.current = onHeaderChange;
  }, [onHeaderChange]);
  useEffect(() => {
    setActiveStepIdRef.current = setActiveStepId;
  }, [setActiveStepId]);

  // Shared: resolve which step is active at the current scroll position and fire header update
  const applyScrollHeader = (el, currentSteps, intlInstance) => {
    if (el.scrollTop < SCROLL_TOP_THRESHOLD) {
      // At top — header should be null
      if (lastEmittedHeaderRef.current !== null) {
        lastEmittedHeaderRef.current = null;
        setActiveStepIdRef.current(null);
        onHeaderChangeRef.current?.(null);
      }

      return;
    }

    const isNearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_BOTTOM_THRESHOLD;

    let activeStep = null;

    for (let i = currentSteps.length - 1; i >= 0; i--) {
      const ref = stepRefs.current[currentSteps[i].id];

      if (ref && (isNearBottom || ref.getBoundingClientRect().top <= HEADER_ACTIVE_OFFSET_PX)) {
        activeStep = currentSteps[i];
        break;
      }
    }

    if (activeStep) {
      const stepIndex = getVisibleStepDisplayIndex(activeStep.id, currentSteps);
      const title = getStepTitle(activeStep, intlInstance);
      const totalSteps = getVisibleCustomizeSteps(currentSteps).length;
      const isMultiSlotStep = isMultiSlotCustomizeStep(activeStep);
      const customizeStepKind = getCustomizeStepSlotKind(activeStep);

      // Only emit if header content changed (dedupe to prevent re-render spam on scroll)
      const last = lastEmittedHeaderRef.current;

      if (
        !last ||
        last.stepId !== activeStep.id ||
        last.stepIndex !== stepIndex ||
        last.title !== title ||
        last.totalSteps !== totalSteps ||
        last.isMultiSlotStep !== isMultiSlotStep ||
        last.customizeStepKind !== customizeStepKind
      ) {
        lastEmittedHeaderRef.current = {
          stepId: activeStep.id,
          stepIndex,
          title,
          totalSteps,
          isMultiSlotStep,
          customizeStepKind,
        };
        setActiveStepIdRef.current(activeStep.id);
        onHeaderChangeRef.current?.({
          isMultiSlotStep,
          customizeStepKind,
          stepIndex,
          title,
          totalSteps,
        });
      }
    } else {
      // No active step found (scrolled past threshold but no step in active zone)
      if (lastEmittedHeaderRef.current !== null) {
        lastEmittedHeaderRef.current = null;
        setActiveStepIdRef.current(null);
        onHeaderChangeRef.current?.(null);
      }
    }
  };

  // Snapshot header from current scroll position whenever activeSlot changes or steps load
  useEffect(() => {
    const el = scrollBodyRef.current;

    if (!el || activeSlot !== null) return;

    const raf = requestAnimationFrame(() => applyScrollHeader(el, stepsRef.current, intl));

    return () => cancelAnimationFrame(raf);
  }, [activeSlot, scrollBodyRef, intl]);

  // Scroll listener: update header as user scrolls on main screen
  useEffect(() => {
    if (activeSlot !== null) return;
    const el = scrollBodyRef.current;

    if (!el) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        applyScrollHeader(el, stepsRef.current, intl);
      });
    };

    el.addEventListener('scroll', onScroll, {passive: true});

    return () => el.removeEventListener('scroll', onScroll);
  }, [activeSlot, scrollBodyRef, intl]);
}
